import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Box,
  Typography,
  Alert,
  CircularProgress,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../utils/config';

interface SubsidiaryInvoiceModalProps {
  open: boolean;
  onClose: () => void;
  billId: string;
  onSuccess: () => void;
}

const CURRENCY_OPTIONS = [
  'USD',
  'EUR',
  'GBP',
  'CAD',
  'AUD',
  'JPY',
  'CNY',
  'INR',
];

export const SubsidiaryInvoiceModal: React.FC<SubsidiaryInvoiceModalProps> = ({
  open,
  onClose,
  billId,
  onSuccess,
}) => {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    invoiceNumber: '',
    totalAmount: '',
    currency: 'USD',
    invoiceDate: new Date().toISOString().split('T')[0],
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'image/bmp',
        'image/webp',
      ];

      if (!allowedTypes.includes(file.type)) {
        setError('Invalid file type. Please upload a PDF, Word document, or image file.');
        return;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB.');
        return;
      }

      setSelectedFile(file);
      setError(null);
    }
  };

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleSubmit = async () => {
    // Validate form
    if (!formData.invoiceNumber || !formData.totalAmount || !formData.invoiceDate || !selectedFile) {
      setError('Please fill in all required fields and select a file.');
      return;
    }

    // Validate total amount is a valid number
    const amount = parseFloat(formData.totalAmount);
    if (isNaN(amount) || amount <= 0) {
      setError('Please enter a valid total amount.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('invoiceNumber', formData.invoiceNumber);
      formDataToSend.append('totalAmount', formData.totalAmount);
      formDataToSend.append('currency', formData.currency);
      formDataToSend.append('invoiceDate', formData.invoiceDate);
      formDataToSend.append('file', selectedFile);

      const response = await authenticatedFetch(
        `${API_URL}/api/accounting/admin/bills/${billId}/subsidiary-invoices`,
        {
          method: 'POST',
          body: formDataToSend,
        }
      );

      if (!response || !response.ok) {
        const errorData = await response?.json().catch(() => null);
        throw new Error(errorData?.error || 'Failed to create subsidiary invoice');
      }

      const result = await response.json();

      // Success - reset form and close modal
      setFormData({
        invoiceNumber: '',
        totalAmount: '',
        currency: 'USD',
        invoiceDate: new Date().toISOString().split('T')[0],
      });
      setSelectedFile(null);
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error creating subsidiary invoice:', err);
      setError(err.message || 'Failed to create subsidiary invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        invoiceNumber: '',
        totalAmount: '',
        currency: 'USD',
        invoiceDate: new Date().toISOString().split('T')[0],
      });
      setSelectedFile(null);
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Add Subsidiary Invoice</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Subsidiary Invoice #"
                fullWidth
                required
                value={formData.invoiceNumber}
                onChange={handleInputChange('invoiceNumber')}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Total Amount"
                fullWidth
                required
                type="number"
                value={formData.totalAmount}
                onChange={handleInputChange('totalAmount')}
                onWheel={(e) => (e.target as HTMLElement).blur()}
                disabled={loading}
                InputProps={{
                  startAdornment: <InputAdornment position="start">{formData.currency}</InputAdornment>,
                }}
                inputProps={{
                  step: '0.01',
                  min: '0',
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Currency"
                fullWidth
                select
                required
                value={formData.currency}
                onChange={handleInputChange('currency')}
                disabled={loading}
              >
                {CURRENCY_OPTIONS.map((currency) => (
                  <MenuItem key={currency} value={currency}>
                    {currency}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Invoice Date"
                fullWidth
                required
                type="date"
                value={formData.invoiceDate}
                onChange={handleInputChange('invoiceDate')}
                disabled={loading}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ mt: 1, mb: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Upload Document *
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  startIcon={<CloudUploadIcon />}
                  disabled={loading}
                  sx={{
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    py: 1.5,
                  }}
                >
                  {selectedFile ? selectedFile.name : 'Choose File (PDF, Word, or Image)'}
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.doc,.docx,image/*"
                    onChange={handleFileChange}
                  />
                </Button>
                {selectedFile && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Size: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !selectedFile}
        >
          {loading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Saving...
            </>
          ) : (
            'Save'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};